import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const avatarVariants = cva('relative flex h-10 w-10 shrink-0 overflow-hidden', {
    variants: {
        variant: {
            default: '', muted: 'bg-muted', dark: 'bg-zinc-900', light: 'bg-zinc-100',
            sidebarSquare: 'bg-sidebar-primary/10', sidebarRound: 'bg-sidebar-primary/10'
        },
        size: { default: '', sm: 'size-8', lg: 'h-11 w-11', sidebarSm: 'size-7', md: 'size-8' },
        shape: { circle: 'rounded-full', square: 'rounded-none', sidebarSquare: 'rounded-lg' },
        border: { none: '', default: 'border border-border', dark: 'border border-zinc-700', 'dark-subtle': 'border border-zinc-800' }
    },
    compoundVariants: [
        { variant: 'sidebarSquare', shape: 'circle', className: 'rounded-lg' },
        { variant: 'sidebarRound', shape: 'circle', className: 'rounded-full' }
    ],
    defaultVariants: { variant: 'default', size: 'default', shape: 'circle', border: 'none' }
});

const Avatar = React.forwardRef<
    React.ComponentRef<typeof AvatarPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarVariants>
>(({ className, variant, size, shape, border, ...props }, ref) => (
    <AvatarPrimitive.Root ref={ref} className={cn(avatarVariants({ variant, size, shape, border }), className)} {...props} />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<React.ComponentRef<typeof AvatarPrimitive.Image>, React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>>(
    ({ className, ...props }, ref) => <AvatarPrimitive.Image ref={ref} className={cn('aspect-square h-full w-full', className)} {...props} />
);
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const avatarFallbackVariants = cva('flex h-full w-full items-center justify-center', {
    variants: {
        variant: {
            default: 'bg-muted', muted: 'bg-muted text-foreground', dark: 'bg-zinc-900 text-zinc-50', light: 'bg-zinc-100 text-black',
            sidebarSquare: 'rounded-lg bg-transparent font-medium text-sidebar-foreground',
            sidebarRound: 'rounded-full bg-transparent font-medium text-sidebar-foreground',
            sidebarRoundPlain: 'rounded-full bg-transparent text-sidebar-foreground'
        },
        shape: { circle: 'rounded-full', square: 'rounded-none' }
    },
    defaultVariants: { variant: 'default', shape: 'circle' }
});

const AvatarFallback = React.forwardRef<
    React.ComponentRef<typeof AvatarPrimitive.Fallback>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & VariantProps<typeof avatarFallbackVariants>
>(({ className, variant, shape, ...props }, ref) => (
    <AvatarPrimitive.Fallback ref={ref} className={cn(avatarFallbackVariants({ variant, shape }), className)} {...props} />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
